import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { ArrowLeftIcon, UserIcon, TrashIcon, KeyIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import Toast from '../components/Toast.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useToast } from '../hooks/useToast.ts';
import api from '../services/api.ts';
import { userService } from '../services/userService.ts';
import FormPage from '../components/ui/FormPage.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
  active: boolean;
  mfaEnabled?: boolean;
  avatar?: FileAttachment[];
  lastLogin?: string;
  createdAt: string;
}

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const resolvedId = id === 'current' ? currentUser?.id : id;
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Query para buscar dados do usuário
  const { data: user, isLoading, error } = useQuery(
    ['user', resolvedId],
    async () => {
      let userData;
      if (id === 'current' || String(id) === String(currentUser?.id)) {
        userData = currentUser;
        setIsOwnProfile(true);
      } else {
        const response = await api.get(`/users/${id}`);
        userData = response.data.data || response.data;
        setIsOwnProfile(false);
      }

      // Converter avatar string para FileAttachment[] se existir
      let avatarFiles: FileAttachment[] = [];
      if (userData.avatar) {
        // Se já é um array, usar como está
        if (Array.isArray(userData.avatar)) {
          avatarFiles = userData.avatar;
        } 
        // Se é uma string (URL), converter para FileAttachment
        else if (typeof userData.avatar === 'string') {
          const avatarUrl = userData.avatar.startsWith('/uploads/') 
            ? getBackendUrl(userData.avatar) 
            : userData.avatar;
          
          avatarFiles = [{
            id: 'existing-avatar',
            name: 'Avatar atual',
            type: 'image' as const,
            file: null,
            url: userData.avatar, // Manter URL original para o backend
            size: -1, // Usar -1 para indicar que é um arquivo existente
            preview: avatarUrl // Usar URL completa para preview
          }];
        }
      }

      return {
        ...userData,
        avatar: avatarFiles
      };
    },
    {
      enabled: resolvedId != null && resolvedId !== '',
      refetchOnWindowFocus: false,
    }
  );

  const [formData, setFormData] = useState<User>({
    id: '',
    name: '',
    email: '',
    role: 'user',
    active: true,
    mfaEnabled: false,
    avatar: [],
    createdAt: ''
  });

  useEffect(() => {
    if (user) {
      const active = typeof (user as any).active === 'boolean'
        ? (user as any).active
        : (user as any).status !== 'inactive';
      setFormData({
        ...user,
        active,
        mfaEnabled: Boolean((user as { mfaEnabled?: boolean }).mfaEnabled),
        avatar: Array.isArray(user.avatar) ? user.avatar : []
      });
    }
  }, [user]);

  const handleAvatarChange = (files: FileAttachment[]) => {
    setFormData(prev => ({
      ...prev,
      avatar: files
    }));
  };

  // Mutation para atualizar usuário
  const updateUserMutation = useMutation(
    async (data: User) => {
      let avatarUrl = '';
      
      // Primeiro, fazer upload do avatar se existir um novo arquivo
      const newAvatarFile = data.avatar?.find(file => file.file instanceof File);
      if (newAvatarFile && newAvatarFile.file) {
        const formDataUpload = new FormData();
        formDataUpload.append('files', newAvatarFile.file);

        const uploadResponse = await api.post('/upload', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          avatarUrl = uploadResponse.data.data.files[0].url;
        }
      } else if (data.avatar && data.avatar.length > 0 && data.avatar[0].url) {
        // Manter avatar existente
        avatarUrl = data.avatar[0].url;
      }

      // Atualizar o usuário
      const numericId = Number(id === 'current' ? currentUser?.id : id);
      if (!numericId || Number.isNaN(numericId)) {
        throw new Error('ID de usuário inválido');
      }
      const userData = {
        name: data.name,
        email: data.email,
        role: data.role,
        active: data.active,
        ...(avatarUrl ? { avatar: avatarUrl } : {}),
      };

      return userService.updateUser(numericId, userData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user', id]);
        queryClient.invalidateQueries('users');
        showSuccess('Usuário atualizado com sucesso!', 'As alterações foram salvas.');
        navigate('/users');
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar usuário:', error);
        const errorMessage = error.response?.data?.message || 'Erro ao atualizar usuário. Tente novamente.';
        showError('Erro ao atualizar usuário', errorMessage);
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateUserMutation.mutate(formData);
  };

  const changePasswordMutation = useMutation(
    async () => {
      const targetId = isOwnProfile ? Number(currentUser?.id) : Number(id);
      if (!targetId || Number.isNaN(targetId)) {
        throw new Error('ID de usuário inválido para alteração de senha');
      }
      return userService.changePassword(targetId, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
    },
    {
      onSuccess: () => {
        showSuccess('Senha alterada com sucesso!', 'Use sua nova senha no próximo login.');
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      },
      onError: (error: any) => {
        console.error('Erro ao alterar senha:', error);
        const errorMessage = error?.response?.data?.error || error?.message || 'Erro ao alterar senha.';
        showError('Falha na alteração de senha', errorMessage);
      },
    }
  );

  const handleDelete = async () => {
    const numericId = Number(id === 'current' ? currentUser?.id : id);
    if (!numericId || Number.isNaN(numericId)) {
      showError('Não foi possível excluir', 'ID de usuário inválido.');
      return;
    }
    try {
      await userService.deleteUser(numericId);
      queryClient.invalidateQueries('users');
      showSuccess('Usuário excluído', 'O usuário foi removido.');
      navigate('/users');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Erro ao excluir usuário.';
      showError('Não foi possível excluir', errorMessage);
    }
  };

  const handlePasswordChange = () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showError('Campos obrigatórios', 'Preencha a senha atual, nova senha e confirmação.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('Senhas não coincidem', 'A nova senha e a confirmação precisam ser iguais.');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showError('Senha fraca', 'A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    changePasswordMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando usuário...</p>
          </div>
        </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Usuário não encontrado</h2>
            <p className="text-gray-600 mb-6">O usuário que você está procurando não existe.</p>
            <button
              onClick={() => navigate('/users')}
              className="btn btn-primary"
            >
              Voltar para Usuários
            </button>
          </div>
        </div>
    );
  }

  return (
    <>
    <FormPage
      backTo="/users"
      backLabel="Voltar para Usuários"
      breadcrumb="Sistema / Usuários / Editar"
      title={isOwnProfile ? 'Meu Perfil' : 'Editar usuário'}
      subtitle={isOwnProfile
        ? 'Gerencie suas informações pessoais e configurações de conta'
        : 'Atualize as informações do usuário no sistema'}
    >
            <form id="edit-user-form" onSubmit={handleSubmit}>
              {/* Avatar Section */}
              <div className="mb-8 text-center">
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Avatar do Usuário</h4>
                  <FileUpload
                    files={formData.avatar || []}
                    onFilesChange={handleAvatarChange}
                    maxFiles={1}
                    maxSizePerFile={5}
                    acceptedTypes={['image/*']}
                    showCategories={false}
                  />
                </div>
                <p className="text-sm text-gray-500">Adicione ou altere a foto de perfil</p>
              </div>

              <div className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
                    Informações Pessoais
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Completo
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        E-mail
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>



                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                        Nível de Acesso
                      </label>
                      <select
                        id="role"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'user' })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="user">Usuário</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        id="status"
                        value={formData.active ? 'active' : 'inactive'}
                        onChange={(e) => setFormData({ ...formData, active: e.target.value === 'active' })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="active">Ativo</option>
                        <option value="inactive">Inativo</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="mfa" className="block text-sm font-medium text-gray-700 mb-2">
                        MFA
                      </label>
                      <input
                        id="mfa"
                        type="text"
                        readOnly
                        aria-readonly="true"
                        value={formData.mfaEnabled ? 'Ativo' : 'Inativo'}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-700"
                      />
                      <p className="text-sm text-gray-500 mt-1">Somente leitura. O MFA é ativado pelo próprio usuário em Segurança.</p>
                    </div>
                  </div>
                </div>

                {/* Security Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 mr-2 text-primary-600" />
                    Segurança
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(true)}
                    className="flex items-center px-4 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <KeyIcon className="h-5 w-5 mr-2" />
                    Alterar Senha
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap justify-end gap-3 pt-6 border-t border-gray-200">
                  {!isOwnProfile && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      Excluir
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate('/users')}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={updateUserMutation.isLoading}
                    className="btn btn-primary"
                  >
                    {updateUserMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </form>
    </FormPage>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Alterar Senha</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha Atual
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nova Senha
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Nova Senha
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={changePasswordMutation.isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changePasswordMutation.isLoading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir usuário"
        message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        onConfirm={() => { setShowDeleteModal(false); handleDelete(); }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Toast Component */}
      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
};

export default EditUser;